import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import SkillDeckLogo from './SkillDeckLogo';
import TemplateGalleryModal from './TemplateGalleryModal';
import OnboardingTutorial, { hasSeenTutorial, resetTutorial } from './OnboardingTutorial';
import CopilotPanel from './CopilotPanel';
import { getStoredTheme, toggleStoredTheme } from '../lib/theme';
import { useCockpitDb } from '../pages/cockpit/CockpitDataContext';
import { toRelance } from '../pages/crm/crmData';

// ─── CSS ────────────────────────────────────────────────────────────────────
const SHELL_CSS = `
:root {
  --bg: #05050F;
  --surface: #0A0A2C;
  --surface2: #10103A;
  --surface3: #161650;
  --vio: #7B68EE;
  --vio2: #A099E8;
  --acc: #C4B5FD;
  --blue: #2563EB;
  --white: #F0EEFF;
  --muted: #5A5580;
  --muted2: #8E88B5;
  --border: #1E1B50;
  --border2: #2A2660;
  --green: #22C55E;
  --yellow: #EAB308;
  --red: #EF4444;
  --orange: #F97316;
  --sidebar-w: 220px;
  --header-h: 56px;
}
.sd-shell *, .sd-shell *::before, .sd-shell *::after { margin: 0; padding: 0; box-sizing: border-box; }
.sd-shell {
  position: fixed; inset: 0; display: flex;
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
  background: var(--bg); color: var(--white);
  font-size: 13px; line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  overflow: hidden;
}
::-webkit-scrollbar { width: 4px; height: 4px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.18); }
.sd-sidebar {
  width: var(--sidebar-w); height: 100%;
  background: var(--surface); border-right: 1px solid var(--border);
  display: flex; flex-direction: column; flex-shrink: 0; z-index: 100;
}
.sd-logo { height: var(--header-h); display: flex; align-items: center; padding: 0 16px; border-bottom: 1px solid var(--border); gap: 9px; flex-shrink: 0; }
.sd-logo-text { font-size: 14px; font-weight: 700; color: var(--white); letter-spacing: -0.4px; }
.sd-logo-text span { color: var(--vio2); }
.sd-nav { flex: 1; padding: 8px 0; overflow-y: auto; }
.sd-nav-section { padding: 0 8px; margin-bottom: 2px; }
.sd-nav-label { font-size: 10px; font-weight: 600; color: var(--muted); letter-spacing: 0.6px; text-transform: uppercase; padding: 10px 10px 4px; }
.sd-nav-item {
  display: flex; align-items: center; gap: 8px; padding: 7px 10px; border-radius: 6px; cursor: pointer;
  color: var(--muted2); font-size: 12.5px; font-weight: 500; transition: background 0.1s, color 0.1s;
  margin-bottom: 1px; position: relative; background: none; border: none; width: 100%; text-align: left; font-family: inherit;
}
.sd-nav-item:hover { background: rgba(255,255,255,0.04); color: var(--white); }
.sd-nav-item.active { background: rgba(123,104,238,0.1); color: var(--vio2); font-weight: 600; }
.sd-nav-item.active::before { content: ''; position: absolute; left: 0; top: 5px; bottom: 5px; width: 2px; background: var(--vio); border-radius: 0 2px 2px 0; }
.sd-nav-icon { width: 16px; height: 16px; flex-shrink: 0; opacity: 0.7; display: flex; align-items: center; justify-content: center; }
.sd-nav-item.active .sd-nav-icon { opacity: 1; }
.sd-nav-badge { margin-left: auto; background: rgba(123,104,238,0.18); color: var(--vio2); font-size: 10px; font-weight: 700; padding: 1px 6px; border-radius: 10px; min-width: 18px; text-align: center; pointer-events: none; }
.sd-nav-item.admin-item { color: #FCD34D; }
.sd-nav-item.admin-item:hover { background: rgba(245,158,11,0.08); color: #FDE68A; }
.sd-nav-item.admin-item.active { background: rgba(245,158,11,0.12); color: #FDE68A; }
.sd-nav-item.admin-item.active::before { background: #F59E0B; }
.sd-nav-caret { margin-left: 6px; display: flex; align-items: center; color: var(--muted2); transition: transform 0.15s; opacity: 0.85; border-radius: 4px; }
.sd-nav-caret.open { transform: rotate(180deg); }
.sd-nav-caret:hover { color: var(--white); }
.sd-nav-sub { display: flex; flex-direction: column; margin: 1px 0 4px; }
.sd-nav-subitem { padding-left: 34px; font-size: 12px; color: var(--muted2); }
.sd-nav-subitem .sd-nav-subdot { position: absolute; left: 19px; width: 4px; height: 4px; border-radius: 50%; background: currentColor; opacity: 0.45; }
.sd-nav-subitem.active { background: rgba(123,104,238,0.1); color: var(--vio2); font-weight: 600; }
.sd-nav-subitem.active .sd-nav-subdot { opacity: 1; }
.sd-usage { padding: 10px 16px 2px; border-top: 1px solid var(--border); }
.sd-usage-label { display: flex; justify-content: space-between; font-size: 10px; color: var(--muted2); margin-bottom: 5px; }
.sd-usage-track { height: 3px; background: rgba(255,255,255,0.07); border-radius: 3px; overflow: hidden; }
.sd-usage-fill { height: 100%; border-radius: 3px; background: linear-gradient(90deg, var(--vio), var(--vio2)); transition: width 0.4s ease; }
.sd-user-area { padding: 8px; }
.sd-user-card { display: flex; align-items: center; gap: 9px; padding: 9px 10px; border-radius: 8px; cursor: pointer; transition: background 0.1s; position: relative; }
.sd-user-card:hover { background: rgba(255,255,255,0.04); }
.sd-avatar { width: 28px; height: 28px; border-radius: 7px; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; flex-shrink: 0; color: white; }
.sd-user-info { flex: 1; min-width: 0; }
.sd-user-name { font-size: 12px; font-weight: 600; color: var(--white); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sd-user-role { font-size: 10.5px; color: var(--muted2); margin-top: 1px; }
.sd-user-menu { position: absolute; bottom: calc(100% + 4px); left: 8px; right: 8px; background: var(--surface2); border: 1px solid var(--border2); border-radius: 9px; padding: 4px; box-shadow: 0 8px 24px rgba(0,0,0,0.4); z-index: 200; }
.sd-user-menu-item { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 6px; cursor: pointer; color: var(--muted2); font-size: 12px; font-weight: 500; transition: background 0.1s, color 0.1s; background: none; border: none; width: 100%; text-align: left; font-family: inherit; }
.sd-user-menu-item:hover { background: rgba(255,255,255,0.05); color: var(--white); }
.sd-user-menu-item.danger:hover { color: #F87171; background: rgba(239,68,68,0.06); }
.sd-main { flex: 1; display: flex; flex-direction: column; height: 100%; overflow: hidden; min-width: 0; }
.sd-header { height: var(--header-h); border-bottom: 1px solid var(--border); display: flex; align-items: center; padding: 0 20px; gap: 12px; flex-shrink: 0; background: var(--surface); }
.sd-page-title { font-size: 14px; font-weight: 600; color: var(--white); letter-spacing: -0.2px; white-space: nowrap; flex-shrink: 0; }
.sd-search-wrap { flex: 1; max-width: 360px; position: relative; }
.sd-search-input { width: 100%; background: var(--bg); border: 1px solid var(--border); border-radius: 7px; padding: 7px 12px 7px 32px; color: var(--white); font-size: 12.5px; outline: none; transition: border-color 0.15s; font-family: inherit; }
.sd-search-input::placeholder { color: var(--muted); }
.sd-search-input:focus { border-color: rgba(123,104,238,0.45); }
.sd-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--muted); }
.sd-header-actions { margin-left: auto; display: flex; align-items: center; gap: 7px; }
.sd-btn { display: inline-flex; align-items: center; gap: 5px; padding: 7px 13px; border-radius: 7px; font-size: 12px; font-weight: 600; cursor: pointer; border: none; transition: all 0.12s; white-space: nowrap; font-family: inherit; }
.sd-btn-primary { background: var(--vio); color: white; }
.sd-btn-primary:hover { background: #8B7CF5; box-shadow: 0 4px 14px rgba(123,104,238,0.3); }
.sd-icon-btn { width: 32px; height: 32px; border-radius: 7px; display: flex; align-items: center; justify-content: center; background: transparent; border: 1px solid var(--border); cursor: pointer; color: var(--muted2); transition: all 0.12s; position: relative; }
.sd-icon-btn:hover { background: var(--surface2); color: var(--white); border-color: var(--border2); }
.sd-notif-dot { position: absolute; top: 5px; right: 5px; width: 7px; height: 7px; background: var(--vio); border-radius: 50%; border: 2px solid var(--surface); }
.sd-content { flex: 1; overflow-y: auto; padding: 20px 22px; width: 100%; min-width: 0; box-sizing: border-box; }
.sd-stats-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px; }
.sd-stat-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 16px 18px; position: relative; overflow: hidden; transition: border-color 0.15s; }
.sd-stat-card:hover { border-color: var(--border2); }
.sd-stat-label { font-size: 11px; color: var(--muted2); font-weight: 500; margin-bottom: 8px; }
.sd-stat-value { font-size: 28px; font-weight: 700; color: var(--white); letter-spacing: -1.2px; margin-bottom: 8px; line-height: 1; }
.sd-stat-delta { font-size: 11px; display: inline-flex; align-items: center; gap: 3px; padding: 2px 7px; border-radius: 6px; font-weight: 500; }
.sd-stat-delta.up { background: rgba(34,197,94,0.08); color: #4ADE80; }
.sd-stat-delta.neutral { background: rgba(167,139,250,0.08); color: var(--vio2); }
.sd-stat-icon { position: absolute; top: 14px; right: 14px; width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; }
.sd-stat-skeleton { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; height: 96px; background: linear-gradient(90deg, var(--surface) 25%, var(--surface2) 50%, var(--surface) 75%); background-size: 200% 100%; animation: sd-shimmer 1.5s infinite; }
@keyframes sd-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
@keyframes sd-spin { to { transform: rotate(360deg); } }

/* ── UX motion (respects prefers-reduced-motion) ─────────────────────── */
@keyframes sd-page-in {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes sd-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes sd-stagger-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes sd-modal-in {
  from { opacity: 0; transform: scale(0.96) translateY(6px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}
@keyframes sd-pop-in {
  0%   { opacity: 0; transform: scale(0.92); }
  60%  { opacity: 1; transform: scale(1.02); }
  100% { transform: scale(1); }
}

@media (prefers-reduced-motion: no-preference) {
  /* Page entry — applied on the .sd-content wrapper and re-keyed by route */
  .sd-page-anim { animation: sd-page-in 0.24s cubic-bezier(0.2, 0.7, 0.3, 1) both; }

  /* Modal entrance */
  .sd-modal-backdrop { animation: sd-fade-in 0.18s ease-out both; }
  .sd-modal-panel    { animation: sd-modal-in 0.22s cubic-bezier(0.2, 0.7, 0.3, 1) both; }

  /* Stagger — apply .sd-stagger on a parent; each child fades in sequentially.
     Capped at 16 children so the last items still appear within ~280ms. */
  .sd-stagger > * { animation: sd-stagger-in 0.32s cubic-bezier(0.2, 0.7, 0.3, 1) both; }
  .sd-stagger > *:nth-child(1)  { animation-delay: 0ms; }
  .sd-stagger > *:nth-child(2)  { animation-delay: 35ms; }
  .sd-stagger > *:nth-child(3)  { animation-delay: 70ms; }
  .sd-stagger > *:nth-child(4)  { animation-delay: 105ms; }
  .sd-stagger > *:nth-child(5)  { animation-delay: 130ms; }
  .sd-stagger > *:nth-child(6)  { animation-delay: 155ms; }
  .sd-stagger > *:nth-child(7)  { animation-delay: 180ms; }
  .sd-stagger > *:nth-child(8)  { animation-delay: 200ms; }
  .sd-stagger > *:nth-child(9)  { animation-delay: 220ms; }
  .sd-stagger > *:nth-child(10) { animation-delay: 235ms; }
  .sd-stagger > *:nth-child(11) { animation-delay: 250ms; }
  .sd-stagger > *:nth-child(12) { animation-delay: 260ms; }
  .sd-stagger > *:nth-child(n+13) { animation-delay: 270ms; }

  /* Pop appearance for icon-state toggles (Copy → Check, etc.) */
  .sd-pop { animation: sd-pop-in 0.32s cubic-bezier(0.2, 0.7, 0.3, 1) both; }

  /* Polished hover transitions on common interactive surfaces */
  .sd-nav-item,
  .sd-stat-card,
  .sd-profile-card,
  .sd-quick-btn,
  .sd-action-btn,
  .sd-icon-btn,
  .sd-btn,
  .sd-filter-tab,
  .sd-dash-section { transition: all 0.18s ease; }

  /* Nudge on click so buttons feel physical */
  .sd-btn:active,
  .sd-icon-btn:active,
  .sd-action-btn:active { transform: translateY(0.5px); }

  /* Card lift on hover */
  .sd-stat-card:hover,
  .sd-profile-card:hover { transform: translateY(-2px); }
}
.sd-dash-grid { display: grid; grid-template-columns: 1fr 340px; gap: 16px; align-items: start; margin-bottom: 24px; }
.sd-dash-left { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.sd-dash-right { display: flex; flex-direction: column; gap: 16px; }
.sd-dash-section { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; overflow: hidden; }
.sd-dash-section-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; border-bottom: 1px solid var(--border); }
.sd-dash-section-title { font-size: 12.5px; font-weight: 600; color: var(--white); letter-spacing: -0.1px; }
.sd-dash-section-count { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 20px; background: rgba(123,104,238,0.12); color: var(--vio2); }
.sd-dash-section-link { font-size: 11px; color: var(--vio2); cursor: pointer; font-weight: 500; padding: 2px 0; background: none; border: none; font-family: inherit; }
.sd-dash-section-link:hover { text-decoration: underline; }
.sd-dash-section-body { padding: 6px 0; }
.sd-empty-state { text-align: center; padding: 36px 20px; }
.sd-empty-icon { font-size: 28px; margin-bottom: 8px; opacity: 0.3; }
.sd-empty-title { font-size: 13px; font-weight: 600; color: var(--muted2); margin-bottom: 4px; }
.sd-empty-sub { font-size: 11.5px; color: var(--muted); }
.sd-activity-item { display: flex; align-items: flex-start; gap: 11px; padding: 9px 18px; border-bottom: 1px solid var(--border); }
.sd-activity-item:last-child { border-bottom: none; }
.sd-activity-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; margin-top: 4px; }
.sd-activity-text { flex: 1; min-width: 0; }
.sd-activity-label { font-size: 12px; color: var(--muted2); line-height: 1.45; }
.sd-activity-label strong { color: var(--white); font-weight: 600; }
.sd-activity-time { font-size: 10px; color: var(--muted); margin-top: 1px; }
.sd-quick { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 14px 16px; }
.sd-quick-title { font-size: 12.5px; font-weight: 600; color: var(--white); margin-bottom: 10px; letter-spacing: -0.1px; }
.sd-quick-btns { display: flex; flex-direction: column; gap: 6px; }
.sd-quick-btn { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-radius: 7px; background: transparent; border: 1px solid var(--border); color: var(--muted2); font-size: 12px; font-weight: 500; cursor: pointer; transition: all 0.12s; text-align: left; font-family: inherit; }
.sd-quick-btn:hover { background: var(--surface2); color: var(--white); border-color: var(--border2); }
.sd-quick-btn.primary { background: rgba(123,104,238,0.1); border-color: rgba(123,104,238,0.25); color: var(--vio2); }
.sd-quick-btn.primary:hover { background: rgba(123,104,238,0.18); }
.sd-toolbar { display: flex; align-items: center; gap: 8px; margin-bottom: 16px; flex-wrap: wrap; }
.sd-filter-tabs { display: flex; gap: 2px; background: var(--surface); border: 1px solid var(--border); border-radius: 8px; padding: 3px; }
.sd-filter-tab { padding: 5px 12px; border-radius: 5px; font-size: 12px; font-weight: 500; cursor: pointer; color: var(--muted2); transition: all 0.12s; white-space: nowrap; background: none; border: none; font-family: inherit; }
.sd-filter-tab:hover { color: var(--white); background: rgba(255,255,255,0.04); }
.sd-filter-tab.active { background: var(--surface3); color: var(--white); font-weight: 600; }
.sd-filter-tab .count { margin-left: 4px; color: var(--muted); font-size: 10.5px; }
.sd-toolbar-right { margin-left: auto; display: flex; align-items: center; gap: 6px; }
.sd-profile-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(275px, 1fr)); gap: 12px; }
.sd-profile-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 16px; cursor: pointer; transition: border-color 0.15s, box-shadow 0.15s; position: relative; overflow: hidden; }
.sd-profile-card:hover { border-color: var(--border2); box-shadow: 0 4px 20px rgba(0,0,0,0.25); }
.sd-card-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 10px; }
.sd-profile-av { width: 36px; height: 36px; border-radius: 9px; font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: white; }
.sd-profile-name { font-size: 13.5px; font-weight: 600; color: var(--white); margin-bottom: 2px; letter-spacing: -0.1px; }
.sd-profile-title { font-size: 11.5px; color: var(--muted2); }
.sd-profile-meta { display: flex; flex-direction: column; gap: 4px; margin: 10px 0; }
.sd-meta-item { display: flex; align-items: center; gap: 4px; font-size: 11px; color: var(--muted2); }
.sd-chip { font-size: 10.5px; font-weight: 500; padding: 2px 8px; border-radius: 5px; background: rgba(123,104,238,0.08); color: var(--vio2); border: 1px solid rgba(123,104,238,0.16); white-space: nowrap; display: inline-block; margin: 2px 2px 2px 0; }
.sd-card-actions { display: flex; gap: 5px; padding-top: 10px; border-top: 1px solid var(--border); }
.sd-action-btn { flex: 1; padding: 6px; border-radius: 6px; display: flex; align-items: center; justify-content: center; gap: 4px; background: transparent; border: 1px solid var(--border); cursor: pointer; color: var(--muted2); font-size: 11.5px; font-weight: 500; transition: all 0.12s; font-family: inherit; }
.sd-action-btn:hover { background: var(--surface2); color: var(--white); border-color: var(--border2); }
.sd-action-btn.danger:hover { background: rgba(239,68,68,0.08); color: #F87171; border-color: rgba(239,68,68,0.2); }
.sd-section-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.sd-section-title { font-size: 14px; font-weight: 700; color: var(--white); letter-spacing: -0.2px; }
.sd-section-sub { font-size: 11.5px; color: var(--muted2); margin-top: 2px; }
.sd-lib-empty { text-align: center; padding: 60px 20px; border: 1.5px dashed rgba(123,104,238,0.18); border-radius: 10px; }
.sd-lib-empty-icon { width: 64px; height: 64px; margin: 0 auto 16px; background: rgba(123,104,238,0.1); border-radius: 50%; display: flex; align-items: center; justify-content: center; }
.sd-lib-empty-title { font-size: 15px; font-weight: 600; color: var(--white); margin-bottom: 6px; }
.sd-lib-empty-sub { font-size: 12px; color: var(--muted2); margin-bottom: 20px; }
.sd-skeleton { background: linear-gradient(90deg, var(--surface) 25%, var(--surface2) 50%, var(--surface) 75%); background-size: 200% 100%; animation: sd-shimmer 1.5s infinite; border-radius: 5px; }

/* ── Forms (dark-theme, reused by Gestion pages) ── */
.sd-page-head { display: flex; flex-direction: column; gap: 4px; margin-bottom: 18px; }
.sd-page-head h1 { font-size: 20px; font-weight: 700; color: var(--white); letter-spacing: -0.4px; margin: 0; }
.sd-page-head p { font-size: 12.5px; color: var(--muted2); margin: 0; }
.sd-form { display: flex; flex-direction: column; gap: 16px; }
.sd-form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px 18px; }
@media (max-width: 720px) { .sd-form-grid { grid-template-columns: 1fr; } }
.sd-field { display: flex; flex-direction: column; gap: 6px; }
.sd-field label { font-size: 11.5px; font-weight: 600; color: var(--muted2); letter-spacing: 0.1px; }
.sd-field label .sd-field-hint { font-weight: 400; color: var(--muted); margin-left: 4px; }
.sd-input, .sd-textarea, .sd-select {
  width: 100%; background: var(--bg); border: 1px solid var(--border);
  border-radius: 7px; padding: 8px 12px; color: var(--white);
  font-size: 12.5px; outline: none; transition: border-color 0.15s, background 0.15s;
  font-family: inherit;
}
.sd-input::placeholder, .sd-textarea::placeholder { color: var(--muted); }
.sd-input:focus, .sd-textarea:focus, .sd-select:focus { border-color: rgba(123,104,238,0.5); background: var(--surface); }
.sd-input:disabled, .sd-textarea:disabled, .sd-select:disabled { background: var(--surface2); color: var(--muted2); cursor: not-allowed; }
.sd-input[readonly] { background: var(--surface2); color: var(--muted2); cursor: default; }
.sd-textarea { resize: vertical; min-height: 80px; }
.sd-select { appearance: none; -webkit-appearance: none; padding-right: 28px;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='10' viewBox='0 0 10 10' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 4l3 3 3-3' stroke='%238E88B5' stroke-width='1.4' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 10px center;
}

/* ── Generic action button family ── */
.sd-btn-ghost { background: transparent; color: var(--muted2); border: 1px solid var(--border); }
.sd-btn-ghost:hover { background: var(--surface2); color: var(--white); border-color: var(--border2); }
.sd-btn-danger { background: transparent; color: #F87171; border: 1px solid rgba(239,68,68,0.3); }
.sd-btn-danger:hover { background: rgba(239,68,68,0.08); border-color: rgba(239,68,68,0.55); }
.sd-btn-soft { background: rgba(123,104,238,0.1); color: var(--vio2); border: 1px solid rgba(123,104,238,0.2); }
.sd-btn-soft:hover { background: rgba(123,104,238,0.18); }

/* ── Small badge/pill primitives used across Gestion ── */
.sd-pill { display: inline-flex; align-items: center; gap: 4px; font-size: 10.5px; font-weight: 600;
  padding: 3px 9px; border-radius: 999px; border: 1px solid var(--border2); background: var(--surface2); color: var(--muted2); }
.sd-pill.success { background: rgba(34,197,94,0.08); color: #4ADE80; border-color: rgba(34,197,94,0.22); }
.sd-pill.warn { background: rgba(234,179,8,0.08); color: #FACC15; border-color: rgba(234,179,8,0.22); }
.sd-pill.danger { background: rgba(239,68,68,0.08); color: #F87171; border-color: rgba(239,68,68,0.22); }
.sd-pill.violet { background: rgba(123,104,238,0.12); color: var(--vio2); border-color: rgba(123,104,238,0.25); }
.sd-pill.blue { background: rgba(37,99,235,0.12); color: #60A5FA; border-color: rgba(37,99,235,0.25); }
.sd-pill.amber { background: rgba(249,115,22,0.12); color: #FB923C; border-color: rgba(249,115,22,0.25); }

/* ── Two-column page layout for Gestion pages ── */
.sd-page-grid { display: grid; grid-template-columns: 1fr 320px; gap: 16px; align-items: start; }
@media (max-width: 1100px) { .sd-page-grid { grid-template-columns: 1fr; } }

/* ── Dropdown menu used in Members row actions ── */
.sd-menu { position: absolute; right: 0; top: calc(100% + 4px); z-index: 50;
  min-width: 200px; background: var(--surface2); border: 1px solid var(--border2);
  border-radius: 9px; padding: 4px; box-shadow: 0 12px 32px rgba(0,0,0,0.45); }
.sd-menu-section { font-size: 10px; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: 0.6px; padding: 8px 10px 4px; }
.sd-menu-item { display: flex; align-items: center; gap: 8px; padding: 7px 10px; border-radius: 6px;
  font-size: 12px; color: var(--muted2); cursor: pointer; background: none; border: none;
  width: 100%; text-align: left; font-family: inherit; transition: background 0.1s, color 0.1s; }
.sd-menu-item:hover { background: rgba(255,255,255,0.05); color: var(--white); }
.sd-menu-item.danger { color: #F87171; }
.sd-menu-item.danger:hover { background: rgba(239,68,68,0.08); color: #FCA5A5; }
.sd-menu-item:disabled { opacity: 0.4; cursor: not-allowed; }
.sd-menu-divider { height: 1px; background: var(--border); margin: 4px 4px; }

/* ── Modal/dialog ── */
.sd-modal-backdrop { position: fixed; inset: 0; z-index: 400; background: rgba(0,0,0,0.6); backdrop-filter: blur(3px); }
.sd-modal { position: fixed; inset: 0; z-index: 401; display: flex; align-items: center; justify-content: center; padding: 16px; pointer-events: none; }
.sd-modal-panel { pointer-events: auto; background: var(--surface); border: 1px solid var(--border2);
  border-radius: 12px; box-shadow: 0 24px 60px rgba(0,0,0,0.55); width: 100%; max-width: 440px; }
.sd-modal-panel.danger { border-color: rgba(239,68,68,0.35); }
.sd-modal-head { padding: 16px 20px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
.sd-modal-title { font-size: 13.5px; font-weight: 700; color: var(--white); letter-spacing: -0.1px; }
.sd-modal-close { background: none; border: none; color: var(--muted2); cursor: pointer; padding: 2px; line-height: 0; font-family: inherit; }
.sd-modal-close:hover { color: var(--white); }
.sd-modal-body { padding: 18px 20px; }
.sd-modal-foot { padding: 14px 20px 16px; display: flex; gap: 8px; justify-content: flex-end; }
@media (max-width: 1100px) { .sd-stats-row { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 1024px) {
  :root { --sidebar-w: 200px; }
  .sd-dash-grid { grid-template-columns: 1fr; }
  .sd-dash-right { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
}
/* Mobile hamburger button — hidden on desktop */
.sd-hamburger {
  display: none;
  width: 32px; height: 32px; border-radius: 7px;
  background: transparent; border: 1px solid var(--border);
  cursor: pointer; color: var(--muted2);
  align-items: center; justify-content: center;
  flex-shrink: 0; transition: all 0.12s;
}
.sd-hamburger:hover { background: var(--surface2); color: var(--white); }

/* Sidebar backdrop overlay */
.sd-sidebar-backdrop {
  display: none;
  position: fixed; inset: 0; z-index: 299;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(2px);
}
.sd-sidebar-backdrop.visible { display: block; }

@media (max-width: 768px) {
  /* Hamburger visible on mobile */
  .sd-hamburger { display: flex; }

  /* Sidebar becomes a slide-in drawer */
  .sd-sidebar {
    position: fixed; left: -100%; top: 0; bottom: 0;
    z-index: 300; width: 260px;
    transition: left 0.25s ease;
    box-shadow: 4px 0 24px rgba(0,0,0,0.4);
  }
  .sd-sidebar--open { left: 0; }

  /* Header: hide search and icon buttons, keep hamburger + title + CTA */
  .sd-search-wrap { display: none; }
  .sd-icon-btn { display: none; }

  /* Grid/layout fixes */
  .sd-stats-row { grid-template-columns: repeat(2, 1fr); gap: 8px; margin-bottom: 12px; }
  .sd-dash-grid { grid-template-columns: 1fr; gap: 10px; }
  .sd-dash-right { display: flex; flex-direction: column; gap: 10px; }
  .sd-profile-grid { grid-template-columns: 1fr; }
  .sd-content { padding: 16px 14px 80px; }

  /* Header tighter on mobile */
  .sd-header { padding: 0 12px; gap: 10px; }
  .sd-header-actions { gap: 6px; }
  .sd-page-title { font-size: 13px; }
}

@media (max-width: 480px) {
  .sd-stats-row { grid-template-columns: 1fr 1fr; gap: 6px; }
  /* Shorten "Nouveau DC" button to icon only */
  .sd-btn-primary .sd-btn-label { display: none; }
  .sd-btn-primary { padding: 7px 10px; }
}
`;

// ─── Helpers ────────────────────────────────────────────────────────────────
const getUserInitials = (email = '') => {
  const local = email.split('@')[0];
  const parts = local.split(/[._-]/);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return local.substring(0, 2).toUpperCase();
};

// ─── SVG Icons ───────────────────────────────────────────────────────────────
const IconFile = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <rect x="1.5" y="2.5" width="12" height="10" rx="2"/>
    <path d="M4.5 6h6M4.5 8.5h4"/>
  </svg>
);
const IconGauge = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="8" r="6.5"/><path d="M8 8l2.5-2.5M5.5 10.8h5"/>
  </svg>
);
const IconTarget = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
    <circle cx="7.5" cy="7.5" r="6"/><circle cx="7.5" cy="7.5" r="3"/><circle cx="7.5" cy="7.5" r=".6" fill="currentColor"/>
  </svg>
);
const IconFlow = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M2 4h7M2 11h7"/><path d="M9 4l2.5 2.5M9 11l2.5-2.5"/>
  </svg>
);
const IconMail = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1.5" y="3" width="13" height="10" rx="2"/><path d="M2 4l6 4.5L14 4"/>
  </svg>
);
const IconBuilding = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="7" height="11" rx="1"/><path d="M9 6h4v7H9"/><path d="M4 5h3M4 7.5h3M4 10h3"/>
  </svg>
);
const IconScope = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
    <circle cx="7.5" cy="7.5" r="6"/><path d="M1.5 7.5h12M7.5 1.5c1.7 2 1.7 10 0 12M7.5 1.5c-1.7 2-1.7 10 0 12"/>
  </svg>
);
const IconTree = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5.5" y="1.5" width="4" height="3" rx="1"/><rect x="1.5" y="10.5" width="4" height="3" rx="1"/>
    <rect x="9.5" y="10.5" width="4" height="3" rx="1"/><path d="M7.5 4.5v3M3.5 10.5V7.5h8v3"/>
  </svg>
);
const IconPlus = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="7.5" cy="7.5" r="6"/><path d="M7.5 4.5v6M4.5 7.5h6"/>
  </svg>
);
const IconSparkle = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7.5 1.5 9 5.8 13.5 7.5 9 9.2 7.5 13.5 6 9.2 1.5 7.5 6 5.8z"/>
  </svg>
);
const IconBriefcase = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <rect x="1.5" y="5.5" width="12" height="8" rx="1.5"/>
    <path d="M5 5.5V4a2 2 0 014 0v1.5M1.5 9.5h12"/>
  </svg>
);
const IconListCheck = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M2 4h11M2 7.5h7.5M2 11h5"/>
    <circle cx="13" cy="11" r="2"/>
    <path d="M12 11l.75.75L14.5 9.5"/>
  </svg>
);
const IconLayout = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <rect x="1" y="1" width="13" height="13" rx="2"/>
    <path d="M1 5.5h13M5.5 5.5v8.5"/>
  </svg>
);
const IconChart = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1.5 11 5.5 7l2.5 3 4.5-5.5"/>
    <path d="M1.5 13.5h12"/>
  </svg>
);
const IconUsers = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="5.5" cy="5" r="2.5"/>
    <path d="M1 13c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4"/>
    <path d="M11 7c1.5 0 3 1 3 3"/>
    <circle cx="11" cy="4" r="2"/>
  </svg>
);
const IconCreditCard = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <rect x="1.5" y="3.5" width="12" height="8" rx="1.5"/>
    <path d="M1.5 6.5h12M4.5 9.5h3"/>
  </svg>
);
const IconSettings = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="7.5" cy="7.5" r="2"/>
    <path d="M7.5 1.5v1.5m0 9v1.5M1.5 7.5H3m9 0h1.5M3.2 3.2l1 1m7.1 7.1 1 1M3.2 11.8l1-1M11.8 3.2l-1 1"/>
  </svg>
);
const IconShield = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7.5 1.5L2 4v4c0 3 2.5 5.5 5.5 6 3-.5 5.5-3 5.5-6V4L7.5 1.5z"/>
  </svg>
);
const IconBell = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M7.5 1.5a5 5 0 015 5v2.5l1 2H1.5l1-2V6.5a5 5 0 015-5z"/>
    <path d="M6 12.5a1.5 1.5 0 003 0"/>
  </svg>
);
const IconSun = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
    <path d="M7.5 1v1.5M7.5 12.5V14M1 7.5h1.5M12.5 7.5H14M3.2 3.2l1 1M10.8 10.8l1 1M3.2 11.8l1-1M10.8 4.2l1-1"/>
    <circle cx="7.5" cy="7.5" r="3"/>
  </svg>
);
const IconMoon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12.5 8.3A5 5 0 016.7 2.5a5 5 0 105.8 5.8z"/>
  </svg>
);
const IconLogout = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M5 2H2.5A1.5 1.5 0 001 3.5v6A1.5 1.5 0 002.5 11H5M8.5 9.5L11 7l-2.5-2.5M11 7H5"/>
  </svg>
);
const IconPlay = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6.5" cy="6.5" r="5.5"/>
    <path d="M5.5 4.5l3 2-3 2v-4z" fill="currentColor"/>
  </svg>
);
const IconDots = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" style={{ flexShrink: 0, color: 'var(--muted)' }}>
    <circle cx="7" cy="3" r=".8" fill="currentColor" stroke="none"/>
    <circle cx="7" cy="7" r=".8" fill="currentColor" stroke="none"/>
    <circle cx="7" cy="11" r=".8" fill="currentColor" stroke="none"/>
  </svg>
);

// ─── AppLayout ───────────────────────────────────────────────────────────────
const AppLayout = ({ children, title, searchQuery, onSearchChange, dossiersCount = 0 }) => {
  const { user, currentOrg, isPlatformAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme);

  // Compteurs CRM affichés en pastille dans la sidebar.
  const { db } = useCockpitDb();
  const comptesCount = (db && db.clients) ? db.clients.length : 0;
  const relanceCount = db ? toRelance(db).length : 0;

  const toggleTheme = () => {
    setTheme(toggleStoredTheme());
  };

  // Auto-launch onboarding tutorial on first visit (per user, persisted in localStorage)
  useEffect(() => {
    if (!user?.id) return;
    if (!hasSeenTutorial(user.id)) {
      // Small delay so layout has time to render before the overlay covers it
      const t = setTimeout(() => setTutorialOpen(true), 400);
      return () => clearTimeout(t);
    }
  }, [user?.id]);

  const replayTutorial = () => {
    setUserMenuOpen(false);
    resetTutorial(user?.id);
    setTutorialOpen(true);
  };

  useEffect(() => {
    if (!document.getElementById('sd-shell-styles')) {
      const style = document.createElement('style');
      style.id = 'sd-shell-styles';
      style.textContent = SHELL_CSS;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    if (title) document.title = `Skilldeck — ${title}`;
  }, [title]);

  const comingSoon = (label = '') => {
    toast({
      title: '🚀 Bientôt disponible',
      description: `${label ? label + ' — ' : ''}Cette fonctionnalité sera disponible prochainement.`,
      duration: 3000,
    });
  };

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    navigate('/');
  };

  const closeMenus = () => {
    if (userMenuOpen) setUserMenuOpen(false);
    setSidebarOpen(false);
  };

  const path = location.pathname;
  const usagePct = Math.min(100, Math.round((dossiersCount / 50) * 100));

  return (
    <div className="sd-shell" onClick={closeMenus}>

      {/* ═══════════════ SIDEBAR ═══════════════ */}
      <aside className={`sd-sidebar${sidebarOpen ? ' sd-sidebar--open' : ''}`}>

        <button
          className="sd-logo"
          onClick={() => { navigate('/cockpit'); setCockpitOpen(true); setSidebarOpen(false); }}
          style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}
          aria-label="Retour au tableau de bord"
        >
          <SkillDeckLogo size={32} />
          <span className="sd-logo-text">Skill<span>Deck</span></span>
        </button>

        <nav className="sd-nav">

          {/* Principal — le pilotage commercial au quotidien */}
          <div className="sd-nav-section">
            <div className="sd-nav-label">Principal</div>
            <button
              className={`sd-nav-item${path === '/cockpit' ? ' active' : ''}`}
              onClick={() => { navigate('/cockpit'); setSidebarOpen(false); }}
            >
              <span className="sd-nav-icon"><IconGauge /></span>
              Tableau de bord
            </button>
            <button
              className={`sd-nav-item${path === '/cockpit/matching' ? ' active' : ''}`}
              onClick={() => { navigate('/cockpit/matching'); setSidebarOpen(false); }}
            >
              <span className="sd-nav-icon"><IconTarget /></span>
              Matching IA
            </button>
            <button
              className={`sd-nav-item${path.startsWith('/cockpit/positionnements') ? ' active' : ''}`}
              onClick={() => { navigate('/cockpit/positionnements'); setSidebarOpen(false); }}
            >
              <span className="sd-nav-icon"><IconFlow /></span>
              Positionnements
            </button>
            <button
              className={`sd-nav-item${path.startsWith('/tenders') ? ' active' : ''}`}
              onClick={() => { navigate('/tenders'); setSidebarOpen(false); }}
            >
              <span className="sd-nav-icon"><IconBriefcase /></span>
              Appels d'offres
            </button>
            <button
              className={`sd-nav-item${path.startsWith('/shortlists') ? ' active' : ''}`}
              onClick={() => { navigate('/shortlists'); setSidebarOpen(false); }}
            >
              <span className="sd-nav-icon"><IconListCheck /></span>
              Shortlists
            </button>
            <button
              className={`sd-nav-item${path.startsWith('/emails') ? ' active' : ''}`}
              onClick={() => { navigate('/emails'); setSidebarOpen(false); }}
            >
              <span className="sd-nav-icon"><IconMail /></span>
              Mails envoyés
            </button>
          </div>

          {/* CRM · le client — la connaissance du compte */}
          <div className="sd-nav-section">
            <div className="sd-nav-label">CRM · le client</div>
            <button
              className={`sd-nav-item${path.startsWith('/crm/comptes') ? ' active' : ''}`}
              onClick={() => { navigate('/crm/comptes'); setSidebarOpen(false); }}
            >
              <span className="sd-nav-icon"><IconBuilding /></span>
              Compte client
              {comptesCount > 0 && <span className="sd-nav-badge">{comptesCount}</span>}
            </button>
            <button
              className={`sd-nav-item${path.startsWith('/crm/perimetre') ? ' active' : ''}`}
              onClick={() => { navigate('/crm/perimetre'); setSidebarOpen(false); }}
            >
              <span className="sd-nav-icon"><IconScope /></span>
              Périmètre
            </button>
            <button
              className={`sd-nav-item${path.startsWith('/crm/cartographie') ? ' active' : ''}`}
              onClick={() => { navigate('/crm/cartographie'); setSidebarOpen(false); }}
            >
              <span className="sd-nav-icon"><IconTree /></span>
              Cartographie
            </button>
          </div>

          {/* Consultant — le dossier de compétences et le vivier */}
          <div className="sd-nav-section">
            <div className="sd-nav-label">Consultant</div>
            <button
              className={`sd-nav-item${(path === '/library' || path.startsWith('/dossier')) ? ' active' : ''}`}
              onClick={() => { navigate('/library'); setSidebarOpen(false); }}
            >
              <span className="sd-nav-icon"><IconFile /></span>
              Bibliothèque de DC
              {dossiersCount > 0 && <span className="sd-nav-badge">{dossiersCount}</span>}
            </button>
            <button
              className="sd-nav-item"
              onClick={() => { setGalleryOpen(true); setSidebarOpen(false); }}
            >
              <span className="sd-nav-icon"><IconLayout /></span>
              Templates
            </button>
            <button
              className={`sd-nav-item${path === '/dossier/new' ? ' active' : ''}`}
              onClick={() => { navigate('/dossier/new'); setSidebarOpen(false); }}
            >
              <span className="sd-nav-icon"><IconPlus /></span>
              Nouveau dossier
            </button>
            <button
              className={`sd-nav-item${path === '/cockpit/consultants' ? ' active' : ''}`}
              onClick={() => { navigate('/cockpit/consultants'); setSidebarOpen(false); }}
            >
              <span className="sd-nav-icon"><IconUsers /></span>
              Vivier consultants
            </button>
          </div>

          {/* Gestion */}
          <div className="sd-nav-section">
            <div className="sd-nav-label">Gestion</div>
            <button
              className={`sd-nav-item${path === '/reporting' ? ' active' : ''}`}
              onClick={() => { navigate('/reporting'); setSidebarOpen(false); }}
            >
              <span className="sd-nav-icon"><IconChart /></span>
              Reporting
              {relanceCount > 0 && (
                <span className="sd-nav-badge" style={{ background: 'rgba(249,115,22,0.15)', color: '#FB923C' }}>
                  {relanceCount}
                </span>
              )}
            </button>
            <button
              className={`sd-nav-item${path === '/members' ? ' active' : ''}`}
              onClick={() => { navigate('/members'); setSidebarOpen(false); }}
            >
              <span className="sd-nav-icon"><IconUsers /></span>
              Membres
            </button>
            <button
              className={`sd-nav-item${path === '/billing' ? ' active' : ''}`}
              onClick={() => { navigate('/billing'); setSidebarOpen(false); }}
            >
              <span className="sd-nav-icon"><IconCreditCard /></span>
              Facturation
            </button>
            <button className={`sd-nav-item${path === '/settings' ? ' active' : ''}`} onClick={() => { navigate('/settings'); setSidebarOpen(false); }}>
              <span className="sd-nav-icon"><IconSettings /></span>
              Paramètres
            </button>
          </div>

          {/* Admin */}
          {isPlatformAdmin && (
            <div className="sd-nav-section">
              <div className="sd-nav-label">Plateforme</div>
              <button
                className={`sd-nav-item admin-item${path.startsWith('/admin') ? ' active' : ''}`}
                onClick={() => { navigate('/admin'); setSidebarOpen(false); }}
              >
                <span className="sd-nav-icon"><IconShield /></span>
                Administration
              </button>
            </div>
          )}

        </nav>

        <div className="sd-usage">
          <div className="sd-usage-label">
            <span>DC utilisés</span>
            <span style={{ color: 'var(--white)', fontWeight: 600 }}>{dossiersCount} / 50</span>
          </div>
          <div className="sd-usage-track">
            <div className="sd-usage-fill" style={{ width: `${usagePct}%` }} />
          </div>
        </div>

        <div className="sd-user-area">
          <div
            className="sd-user-card"
            onClick={(e) => { e.stopPropagation(); setUserMenuOpen((v) => !v); }}
          >
            <div className="sd-avatar" style={{ background: 'linear-gradient(135deg,#7B68EE,#2563EB)' }}>
              {getUserInitials(user?.email || '')}
            </div>
            <div className="sd-user-info">
              <div className="sd-user-name">{user?.email?.split('@')[0] || '—'}</div>
              <div className="sd-user-role">{currentOrg?.name || '—'}</div>
            </div>
            <IconDots />
            {userMenuOpen && (
              <div className="sd-user-menu" onClick={(e) => e.stopPropagation()}>
                <button className="sd-user-menu-item" onClick={replayTutorial}>
                  <IconPlay />
                  Revoir le tutoriel
                </button>
                <button className="sd-user-menu-item danger" onClick={handleLogout}>
                  <IconLogout />
                  Se déconnecter
                </button>
              </div>
            )}
          </div>
        </div>

      </aside>

      {/* ═══════════════ MAIN ═══════════════ */}
      <div className="sd-main">

        <header className="sd-header">
          <button
            className="sd-hamburger"
            onClick={(e) => { e.stopPropagation(); setSidebarOpen(true); }}
            aria-label="Ouvrir le menu"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M2 4h12M2 8h12M2 12h12"/>
            </svg>
          </button>
          <div className="sd-page-title">{title}</div>

          {onSearchChange && (
            <div className="sd-search-wrap">
              <span className="sd-search-icon">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <circle cx="5.5" cy="5.5" r="4"/><path d="M9 9 12 12"/>
                </svg>
              </span>
              <input
                className="sd-search-input"
                type="text"
                placeholder="Rechercher un profil, une technologie…"
                value={searchQuery || ''}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
          )}

          <div className="sd-header-actions">
            <button
              className="sd-icon-btn"
              title={theme === 'light' ? 'Passer en mode sombre' : 'Passer en mode clair'}
              onClick={toggleTheme}
            >
              {theme === 'light' ? <IconMoon /> : <IconSun />}
            </button>
            <button className="sd-icon-btn" title="Notifications" onClick={() => comingSoon('Notifications')}>
              <IconBell />
              <span className="sd-notif-dot" />
            </button>
            <button className="sd-btn sd-btn-primary" onClick={() => navigate('/dossier/new')}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 1v10M1 6h10"/>
              </svg>
              <span className="sd-btn-label">Nouveau DC</span>
            </button>
          </div>
        </header>

        <div key={location.pathname} className="sd-content sd-page-anim">
          {children}
        </div>

      </div>

      {sidebarOpen && (
        <div
          className="sd-sidebar-backdrop visible"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Template gallery (sidebar-launched) ──
          From here we don't have a current dossier in scope, so picking a
          template starts a new one with that template pre-selected. */}
      <TemplateGalleryModal
        open={galleryOpen}
        currentTemplate={null}
        onClose={() => setGalleryOpen(false)}
        onSelect={(templateId) => {
          setGalleryOpen(false);
          navigate(`/dossier/new?template=${encodeURIComponent(templateId)}`);
        }}
      />

      {/* ── First-visit onboarding tutorial (replayable from user menu) ── */}
      <OnboardingTutorial
        open={tutorialOpen}
        onClose={() => setTutorialOpen(false)}
      />

      {/* ── Copilot — available from every page of the app (⌘K) ── */}
      <CopilotPanel />
    </div>
  );
};

export default AppLayout;
